import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Settings, Upload, Save, Loader2, Store,
  Palette, Globe2, Phone, MapPin, Receipt, Bike, Check, Wifi, WifiOff, Percent
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_COLORS = {
  grab: '#00B14F', foodpanda: '#D70F64', line_man: '#00C300',
  uber_eats: '#000000', deliveroo: '#00CCBC', just_eat: '#FF8000',
  glovo: '#FFC244', wolt: '#009DE0', bolt_food: '#34D186',
  doordash: '#FF3008', grubhub: '#F63440',
  talabat: '#FF5800', careem: '#49B44D', noon_food: '#FEEE00',
  rappi: '#FF441F', ifood: '#EA1D2C', custom_delivery: '#8B5CF6',
};

export default function SettingsPage() {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [availablePlatforms, setAvailablePlatforms] = useState({});
  const [activePlatforms, setActivePlatforms] = useState([]);
  const [platformCommissions, setPlatformCommissions] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [settingsRes, platformsRes] = await Promise.all([
          fetch(`${API}/settings/restaurant`, { headers }),
          fetch(`${API}/settings/delivery-platforms`, { headers }),
        ]);
        const data = await settingsRes.json();
        setSettings(data);
        setActivePlatforms(data.delivery_platforms || []);
        setPlatformCommissions(data.platform_commissions || {});
        setForm({
          name: data.name || '',
          currency: data.currency || 'THB',
          timezone: data.timezone || 'Asia/Bangkok',
          primary_color: data.primary_color || '#D4A853',
          secondary_color: data.secondary_color || '#0A422D',
          phone: data.phone || '',
          address: data.address || '',
          tax_rate: data.tax_rate || 0,
          service_charge: data.service_charge || 0,
        });
        if (platformsRes.ok) setAvailablePlatforms(await platformsRes.json());
      } catch { /* ignore */ } finally { setLoading(false); }
    };
    fetchSettings();
  }, [token]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/settings/restaurant`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, delivery_platforms: activePlatforms, platform_commissions: platformCommissions }),
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        toast.success('Parametres enregistres !');
      }
    } catch { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/settings/restaurant/logo`, {
        method: 'POST', headers, body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({ ...prev, logo_url: data.logo_url }));
        toast.success('Logo mis a jour !');
      }
    } catch { toast.error('Erreur upload'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0C0C0C] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#D4A853] animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0C0C0C] text-white" data-testid="settings-page">
      <header className="border-b border-[#1E1E1E] sticky top-0 z-40 bg-[#0C0C0C]/95 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link to="/dashboard" className="text-[#666] hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
          <Settings className="w-5 h-5 text-[#D4A853]" />
          <span className="font-bold text-lg">Parametres du restaurant</span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Logo */}
        <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-6" data-testid="settings-logo">
          <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Store className="w-4 h-4 text-[#D4A853]" /> Logo</h3>
          <div className="flex items-center gap-4">
            {settings?.logo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}`} alt="Logo"
                className="w-20 h-20 rounded-xl object-cover border border-[#333]" />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-[#1A1A1A] border border-[#333] flex items-center justify-center">
                <Store className="w-8 h-8 text-[#666]" />
              </div>
            )}
            <label className="cursor-pointer">
              <Button variant="outline" className="border-[#333] text-white hover:bg-[#222]" asChild>
                <span><Upload className="w-4 h-4 mr-2" /> Changer le logo</span>
              </Button>
              <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-upload" />
            </label>
          </div>
        </div>

        {/* General Info */}
        <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-6" data-testid="settings-general">
          <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Store className="w-4 h-4 text-[#D4A853]" /> Informations generales</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#888] mb-1 block">Nom du restaurant</label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="bg-[#0C0C0C] border-[#333] text-white" data-testid="settings-name" />
            </div>
            <div>
              <label className="text-xs text-[#888] mb-1 block flex items-center gap-1"><Phone className="w-3 h-3" /> Telephone</label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                className="bg-[#0C0C0C] border-[#333] text-white" data-testid="settings-phone" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-[#888] mb-1 block flex items-center gap-1"><MapPin className="w-3 h-3" /> Adresse</label>
              <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})}
                className="bg-[#0C0C0C] border-[#333] text-white" data-testid="settings-address" />
            </div>
          </div>
        </div>

        {/* Regional */}
        <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-6" data-testid="settings-regional">
          <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Globe2 className="w-4 h-4 text-[#D4A853]" /> Regional</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#888] mb-1 block">Devise</label>
              <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}
                className="w-full h-10 px-3 bg-[#0C0C0C] border border-[#333] rounded-md text-white text-sm" data-testid="settings-currency">
                <option value="THB">THB - Baht</option>
                <option value="EUR">EUR - Euro</option>
                <option value="USD">USD - Dollar</option>
                <option value="GBP">GBP - Livre</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-[#888] mb-1 block">Fuseau horaire</label>
              <select value={form.timezone} onChange={e => setForm({...form, timezone: e.target.value})}
                className="w-full h-10 px-3 bg-[#0C0C0C] border border-[#333] rounded-md text-white text-sm" data-testid="settings-timezone">
                <option value="Asia/Bangkok">Asia/Bangkok (UTC+7)</option>
                <option value="Europe/Paris">Europe/Paris (UTC+1)</option>
                <option value="America/New_York">America/New_York (UTC-5)</option>
                <option value="Asia/Tokyo">Asia/Tokyo (UTC+9)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Appearance */}
        <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-6" data-testid="settings-appearance">
          <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Palette className="w-4 h-4 text-[#D4A853]" /> Apparence</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#888] mb-1 block">Couleur principale</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})}
                  className="w-10 h-10 rounded border-0 cursor-pointer" data-testid="settings-primary-color" />
                <Input value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})}
                  className="bg-[#0C0C0C] border-[#333] text-white flex-1" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#888] mb-1 block">Couleur secondaire</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.secondary_color} onChange={e => setForm({...form, secondary_color: e.target.value})}
                  className="w-10 h-10 rounded border-0 cursor-pointer" data-testid="settings-secondary-color" />
                <Input value={form.secondary_color} onChange={e => setForm({...form, secondary_color: e.target.value})}
                  className="bg-[#0C0C0C] border-[#333] text-white flex-1" />
              </div>
            </div>
          </div>
        </div>

        {/* Billing */}
        <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-6" data-testid="settings-billing">
          <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Receipt className="w-4 h-4 text-[#D4A853]" /> Facturation</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#888] mb-1 block">Taxe (%)</label>
              <Input type="number" value={form.tax_rate} onChange={e => setForm({...form, tax_rate: parseFloat(e.target.value) || 0})}
                className="bg-[#0C0C0C] border-[#333] text-white" data-testid="settings-tax" />
            </div>
            <div>
              <label className="text-xs text-[#888] mb-1 block">Service charge (%)</label>
              <Input type="number" value={form.service_charge} onChange={e => setForm({...form, service_charge: parseFloat(e.target.value) || 0})}
                className="bg-[#0C0C0C] border-[#333] text-white" data-testid="settings-service" />
            </div>
          </div>
        </div>

        {/* Delivery Platforms */}
        <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-6" data-testid="settings-platforms">
          <h3 className="font-bold text-sm mb-2 flex items-center gap-2"><Bike className="w-4 h-4 text-[#D4A853]" /> Plateformes de livraison</h3>
          <p className="text-xs text-[#666] mb-4">Selectionnez les plateformes que vous utilisez. Elles apparaitront dans l'interface serveur.</p>

          {Object.entries(availablePlatforms).map(([regionKey, regionData]) => (
            <div key={regionKey} className="mb-4 last:mb-0">
              <p className="text-xs text-[#888] font-medium mb-2 uppercase tracking-wider">{regionData.label}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {regionData.platforms.map(platform => {
                  const isActive = activePlatforms.includes(platform.id);
                  const color = PLATFORM_COLORS[platform.id] || '#888';
                  return (
                    <button
                      key={platform.id}
                      type="button"
                      onClick={() => {
                        setActivePlatforms(prev =>
                          isActive ? prev.filter(p => p !== platform.id) : [...prev, platform.id]
                        );
                      }}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 transition-all text-left ${
                        isActive
                          ? 'border-current bg-current/10'
                          : 'border-[#2A2A2A] bg-[#0C0C0C] hover:border-[#444]'
                      }`}
                      style={isActive ? { borderColor: color, backgroundColor: `${color}15` } : {}}
                      data-testid={`platform-${platform.id}`}
                    >
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className={`text-xs font-medium flex-1 ${isActive ? 'text-white' : 'text-[#888]'}`}>
                        {platform.name}
                      </span>
                      {isActive && <Check className="w-3 h-3 flex-shrink-0" style={{ color }} />}
                      {platform.has_api && (
                        <span title="API disponible">
                          {isActive ? <Wifi className="w-3 h-3 text-[#888]" /> : <WifiOff className="w-3 h-3 text-[#444]" />}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {activePlatforms.length > 0 && (
            <div className="mt-4 pt-3 border-t border-[#2A2A2A]">
              <p className="text-xs text-[#888] mb-3 font-medium">
                <Percent className="w-3 h-3 inline mr-1" />
                Commission par plateforme (les prix seront majores automatiquement)
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {activePlatforms.map(p => {
                  const allPlatforms = Object.values(availablePlatforms).flatMap(r => r.platforms);
                  const found = allPlatforms.find(x => x.id === p);
                  const color = PLATFORM_COLORS[p] || '#888';
                  return (
                    <div key={p} className="flex items-center gap-2 bg-[#0C0C0C] rounded-lg px-3 py-2 border border-[#2A2A2A]">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-xs text-[#888] flex-1">{found?.name || p}</span>
                      <input type="number" min="0" max="50" step="0.5"
                        value={platformCommissions[p] || ''}
                        onChange={(e) => setPlatformCommissions(prev => ({ ...prev, [p]: parseFloat(e.target.value) || 0 }))}
                        placeholder="0"
                        className="w-14 h-7 text-xs text-center bg-[#111] border border-[#333] text-white rounded-md"
                        data-testid={`commission-${p}`} />
                      <span className="text-[10px] text-[#666]">%</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-[#888] mt-2">
                <span className="text-[#D4A853] font-medium">{activePlatforms.length}</span> plateforme{activePlatforms.length > 1 ? 's' : ''} active{activePlatforms.length > 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>

        {/* Save Button */}
        <Button onClick={saveSettings} disabled={saving}
          className="bg-[#D4A853] hover:bg-[#C49A48] text-black font-bold h-12 px-8 rounded-xl" data-testid="settings-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer les parametres
        </Button>
      </div>
    </div>
  );
}
